/**
 * Profile Mutation Hooks
 *
 * TanStack Query mutation hooks for profile operations
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/shared/stores/auth-store';
import type {
  ShareLocationData,
  UpdateContactData,
  UpdateEmailData,
  UpdatePasswordData,
  UpdateProfileData,
} from '../client/profile.client';
import { profileClient } from '../client/profile.client';

/**
 * Hook to update profile
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateProfileData) => profileClient.updateProfile(data),
    onSuccess: (updatedUser) => {
      queryClient.setQueryData(['profile'], updatedUser);
      queryClient.invalidateQueries({ queryKey: ['auth', 'user', 'me'] });
    },
  });
}

/**
 * Hook to update display picture
 */
export function useUpdateAvatar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => profileClient.updateDisplayPicture(file),
    onSuccess: (updatedUser) => {
      queryClient.setQueryData(['profile'], updatedUser);
      queryClient.invalidateQueries({ queryKey: ['auth', 'user', 'me'] });
    },
  });
}

/**
 * Hook to update email
 */
export function useUpdateEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateEmailData) => profileClient.updateEmail(data),
    onSuccess: (updatedUser) => {
      queryClient.setQueryData(['profile'], updatedUser);
      queryClient.invalidateQueries({ queryKey: ['auth', 'user', 'me'] });
    },
  });
}

/**
 * Hook to update contact info
 */
export function useUpdateContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateContactData) => profileClient.updateContact(data),
    onSuccess: (updatedUser) => {
      queryClient.setQueryData(['profile'], updatedUser);
      queryClient.invalidateQueries({ queryKey: ['auth', 'user', 'me'] });
    },
  });
}

/**
 * Hook to update password
 */
export function useUpdatePassword() {
  return useMutation({
    mutationFn: (data: UpdatePasswordData) => profileClient.updatePassword(data),
  });
}

/**
 * Hook to persist the user's subtitle/caption language preference.
 *
 * BE response is a flat `{ message: "" }` ack — no User envelope. Optimistic
 * `onMutate` IS the source of truth on the FE; success is a no-op, error
 * rolls back. `/me` is not TanStack-backed, so the store patch is direct.
 */
export function useUpdateSubtitleLanguage() {
  return useMutation({
    mutationKey: ['profile', 'subtitle-language'],
    mutationFn: (lang: string | null) => profileClient.updateSubtitleLanguage(lang),
    onMutate: (next) => {
      const prev = useAuthStore.getState().user?.subtitle_language ?? null;
      useAuthStore.getState().updateUser({ subtitle_language: next });
      return { prev };
    },
    onError: (_err, _next, ctx) => {
      if (ctx) {
        useAuthStore.getState().updateUser({ subtitle_language: ctx.prev });
      }
    },
  });
}

/**
 * Hook to sign the creator contract.
 *
 * BE returns a flat `{ creator_contract_date }` payload — patch the
 * auth-store directly with the new date so the gate unmounts. `/me` is not
 * TanStack-backed (it's fetched via auth-store actions), so there's no
 * query key to invalidate here.
 */
export function useSignCreatorContract() {
  return useMutation({
    mutationFn: () => profileClient.signCreatorContract(),
    onSuccess: (response) => {
      useAuthStore.getState().updateUser({
        creator_contract_date: response.creator_contract_date,
      });
    },
  });
}

/**
 * Hook to opt in to location sharing (globe heatmap).
 *
 * BE returns the snapped `location` object directly — patch the auth-store so
 * the opt-in state survives reload. `/me` is not TanStack-backed, so there's
 * no query key to invalidate. Per-call `onError` at the call site drives the
 * prompt UI state (422 physics-violation / 429 throttle → dismiss, no retry).
 */
export function useShareLocation() {
  return useMutation({
    mutationFn: (data: ShareLocationData) => profileClient.shareLocation(data),
    onSuccess: (location) => {
      useAuthStore.getState().updateUser({ location });
    },
  });
}
